import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LogOut, Home, History, Ruler, Palette, MessageCircle, Gift, Star, Package, ChevronDown, ChevronRight, Plus, Trash2, Download, Phone, Mail, Users, MapPin, Paintbrush, Upload, Loader2, Coins, CreditCard } from 'lucide-react';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import PremiumDashboard from './PremiumDashboard';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://www.mypanther.fr/web/image/website/1/logo/My%20Panther?unique=501ee63";
const EMPTY_LINE = { nom: '', numero: '', taille: '' };

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [clientInfo, setClientInfo] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [history, setHistory] = useState([]);
  const [commercialInfo, setCommercialInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [expandedYears, setExpandedYears] = useState({});
  const [isPremium, setIsPremium] = useState(false);

  // Personalization
  const [persoLines, setPersoLines] = useState([{ ...EMPTY_LINE }]);
  const [persoExporting, setPersoExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [meRes, dashRes, histRes, comRes] = await Promise.all([
        axios.get(`${API}/api/client/me`, { withCredentials: true }),
        axios.get(`${API}/api/client/dashboard`, { withCredentials: true }),
        axios.get(`${API}/api/client/orders/history`, { withCredentials: true }),
        axios.get(`${API}/api/client/commercial-info`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setClientInfo(meRes.data);
      setIsPremium(!!meRes.data?.premium);
      setDashboard(dashRes.data);
      setHistory(histRes.data);
      setCommercialInfo(comRes.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/espace-client');
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => {
    await axios.post(`${API}/api/client/logout`, {}, { withCredentials: true });
    navigate('/espace-client');
  };

  const handleClaim = async () => {
    try {
      const res = await axios.post(`${API}/api/client/loyalty/claim`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handlePersoExport = async () => {
    const validLines = persoLines.filter(l => l.nom.trim());
    if (!validLines.length) { toast.error('Ajoutez au moins un nom'); return; }
    setPersoExporting(true);
    try {
      const res = await axios.post(`${API}/api/personalization/export`, {
        client_name: `${clientInfo?.firstname || ''} ${clientInfo?.name || ''}`.trim(),
        lines: validLines,
      }, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = 'personnalisation.xlsx'; document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error('Erreur export'); }
    finally { setPersoExporting(false); }
  };

  const historyByYear = {};
  history.forEach(o => { const yr = o.year || 'Inconnu'; if (!historyByYear[yr]) historyByYear[yr] = []; historyByYear[yr].push(o); });
  const sortedYears = Object.keys(historyByYear).sort((a, b) => b - a);

  const sidebarItems = [
    { id: 'home', label: 'Accueil', icon: Home },
    { id: 'history', label: 'Archives', icon: History },
    { id: 'size-guide', label: 'Guide des tailles', icon: Ruler },
    { id: 'personalization', label: 'Personnalisation', icon: Palette },
    { id: 'contact', label: 'Contact', icon: MessageCircle },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>;

  if (isPremium) return <PremiumDashboard />;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Panther" className="h-9 object-contain" />
            <div>
              <p className="text-sm font-medium text-slate-900">{clientInfo?.firstname} {clientInfo?.name}</p>
              <p className="text-xs text-slate-500">{clientInfo?.company_name} — Espace Client</p>
            </div>
          </div>
          <Button data-testid="client-logout-btn" onClick={handleLogout} variant="ghost" className="text-slate-600 text-sm">
            <LogOut size={16} className="mr-2" /> Déconnexion
          </Button>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-56 bg-white border-r border-slate-200 flex-shrink-0" data-testid="client-sidebar">
          {clientInfo?.logo_url && (
            <div className="p-4 border-b border-slate-100 flex justify-center">
              <img src={clientInfo.logo_url} alt={clientInfo.company_name || 'Logo'} className="max-h-20 max-w-[180px] object-contain" data-testid="client-logo" />
            </div>
          )}
          <nav className="p-3 space-y-1">
            {sidebarItems.map(item => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button key={item.id} data-testid={`client-tab-${item.id}`} onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                  <Icon size={18} /> <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 p-6 space-y-6 overflow-auto">

          {/* ===== ACCUEIL ===== */}
          {activeTab === 'home' && dashboard && (
            <div className="space-y-6">
              {/* Stats + Address row */}
              <div className="flex gap-4">
                <div className="grid grid-cols-3 gap-4 flex-1">
                  <Card className="border-slate-200">
                    <CardContent className="pt-4 text-center">
                      <Package size={20} className="mx-auto text-blue-600 mb-1" />
                      <p className="text-2xl font-bold text-slate-900">{dashboard.active_orders?.length || 0}</p>
                      <p className="text-xs text-slate-500">Commandes en cours</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="pt-4 text-center">
                      <Star size={20} className="mx-auto text-amber-500 mb-1" />
                      <p className="text-2xl font-bold text-slate-900">{(dashboard.available_points || 0).toLocaleString()}</p>
                      <p className="text-xs text-slate-500">Points fidélité</p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="pt-4 text-center">
                      <History size={20} className="mx-auto text-emerald-600 mb-1" />
                      <p className="text-2xl font-bold text-slate-900">{dashboard.total_orders || 0}</p>
                      <p className="text-xs text-slate-500">Commandes totales</p>
                    </CardContent>
                  </Card>
                </div>
                {(clientInfo?.address || clientInfo?.city || clientInfo?.country) && (
                  <Card className="border-slate-200 w-72 flex-shrink-0">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-2">
                        <MapPin size={16} className="text-slate-500 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-slate-700">
                          <p className="font-semibold text-slate-900 mb-1 text-sm">Adresse de livraison</p>
                          <p className="font-medium">{clientInfo.firstname} {clientInfo.name}</p>
                          {clientInfo.address && <p>{clientInfo.address}</p>}
                          <p>{[clientInfo.postal_code, clientInfo.city].filter(Boolean).join(' ')}</p>
                          {clientInfo.country && <p>{clientInfo.country}</p>}
                          {clientInfo.phone && <p className="mt-1">Tél : {clientInfo.phone}</p>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Active Orders */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Commandes en cours ({dashboard.active_orders?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {(!dashboard.active_orders || dashboard.active_orders.length === 0) ? (
                    <p className="text-sm text-slate-400 text-center py-4">Aucune commande en cours</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-medium">Date</TableHead>
                          <TableHead className="font-medium">Commande</TableHead>
                          <TableHead className="font-medium">Statut</TableHead>
                          <TableHead className="font-medium">Suivi</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dashboard.active_orders.map(o => (
                          <TableRow key={o._id}>
                            <TableCell className="text-xs">{o.order_date?.slice(0, 10)}</TableCell>
                            <TableCell className="font-medium text-sm">{o.order_number}</TableCell>
                            <TableCell><Badge className="bg-blue-100 text-blue-800 text-xs">{(o.status || '').toUpperCase()}</Badge></TableCell>
                            <TableCell>
                              {o.tracking_number ? (
                                <Button size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => window.open(`${window.location.origin}/track?n=${o.tracking_number}`, '_blank')}>
                                  <Package size={12} /> Suivi
                                </Button>
                              ) : <span className="text-xs text-slate-400">—</span>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Parrainage + Fidélité side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Parrainage */}
                <Card className="border-blue-200 bg-blue-50/30">
                  <CardContent className="pt-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Users size={22} className="text-blue-600" />
                      <h3 className="font-semibold text-slate-900">Parrainage</h3>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      Parrainez un autre club et gagnez <strong className="text-blue-700">200€ de bons d'achat</strong> chez Panther !
                    </p>
                    <p className="text-xs text-slate-500 mt-3">
                      Parlez de nous à d'autres clubs autour de vous. Dès qu'ils passent leur première commande en mentionnant votre nom, vous recevez 200€ de bons d'achat valables sur votre prochaine commande.
                    </p>
                    {commercialInfo?.name && (
                      <p className="text-xs text-slate-500 mt-3 italic">
                        Contactez votre commercial <strong>{commercialInfo.name}</strong> pour en savoir plus.
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Fidélité */}
                <Card className="border-amber-200 bg-amber-50/30">
                  <CardContent className="pt-5 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Gift size={22} className="text-amber-600" />
                        <h3 className="font-semibold text-slate-900">Programme de fidélité</h3>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">
                        Plus vous commandez, plus vous gagnez de points ! À <strong>15 000 points</strong>, recevez <strong>10 maillots ou t-shirts personnalisés offerts</strong>.
                      </p>
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-slate-500">Progression</span>
                          <span className="text-xs font-bold text-amber-700">{(dashboard.available_points || 0).toLocaleString()} / 15 000</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-3">
                          <div className="h-3 rounded-full bg-amber-500 transition-all" style={{ width: `${Math.min(((dashboard.available_points || 0) / 15000) * 100, 100)}%` }} />
                        </div>
                      </div>
                    </div>
                    {(dashboard.available_points || 0) >= 15000 && (
                      <Button data-testid="client-claim-btn" className="bg-amber-600 hover:bg-amber-700 text-white gap-2 mt-4 w-full" onClick={handleClaim}>
                        <Gift size={16} /> Réclamer mon lot
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Conditions de paiement */}
              <Card className="border-slate-200">
                <CardContent className="pt-5">
                  <h3 className="font-semibold text-slate-900 mb-3" style={{ color: '#c3263b' }}>Conditions de paiement & Frais</h3>
                  <div className="bg-emerald-50/50 border border-emerald-100 p-5 rounded-xl text-sm">
                    <p className="font-semibold">Modalités :</p>
                    <ul className="mt-2 ml-5 list-disc space-y-1">
                      <li><strong>50% d'acompte</strong> à la validation de la commande.</li>
                      <li><strong>Le solde</strong> lorsque la fabrication est terminée.</li>
                    </ul>
                    <p className="mt-4 text-xs text-slate-600">
                      Nos prix incluent les <strong>frais de livraison standard</strong> (pour les commandes supérieures à 1400€ HT) ainsi que les <strong>frais de dédouanement</strong>.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Réseaux sociaux */}
              <Card className="border-slate-200">
                <CardContent className="pt-5">
                  <h3 className="font-semibold text-slate-900 mb-4">Rejoignez-nous sur les réseaux</h3>
                  <div className="flex gap-4">
                    <a href="https://www.instagram.com/pantherdesignforyou/" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-pink-50 hover:border-pink-300 transition-colors text-sm font-medium text-slate-700">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-pink-600"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                      Instagram
                    </a>
                    <a href="https://www.facebook.com/PantherDesignForYou" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition-colors text-sm font-medium text-slate-700">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="text-blue-600"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
                      Facebook
                    </a>
                    <a href="https://www.youtube.com/@pantherdesign" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-red-50 hover:border-red-300 transition-colors text-sm font-medium text-slate-700">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="text-red-600"><path d="M22.54 6.42a2.78 2.78 0 00-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 2A29 29 0 001 11.75a29 29 0 00.46 5.33A2.78 2.78 0 003.4 19.1c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 001.94-2 29 29 0 00.46-5.25 29 29 0 00-.46-5.43z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" fill="white"/></svg>
                      YouTube
                    </a>
                    <a href="https://www.tiktok.com/@pantherfr" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-100 hover:border-slate-400 transition-colors text-sm font-medium text-slate-700">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" className="text-slate-900"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.73a8.19 8.19 0 004.76 1.52V6.8a4.84 4.84 0 01-1-.11z"/></svg>
                      TikTok
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}


          {/* ===== ARCHIVES ===== */}
          {activeTab === 'history' && (
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base">Archives des commandes</CardTitle></CardHeader>
              <CardContent>
                {sortedYears.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Aucune commande livrée</p>
                ) : (
                  <div className="space-y-3">
                    {sortedYears.map(year => (
                      <div key={year} className="border border-slate-200 rounded-lg overflow-hidden">
                        <button className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left"
                          onClick={() => setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }))}>
                          <div className="flex items-center gap-2">
                            {expandedYears[year] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            <span className="font-semibold text-slate-900">{year}</span>
                          </div>
                          <Badge className="bg-slate-200 text-slate-700 border-0">{historyByYear[year].length}</Badge>
                        </button>
                        {expandedYears[year] && (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="font-medium">Commande</TableHead>
                                <TableHead className="font-medium">Statut</TableHead>
                                <TableHead className="font-medium">Suivi</TableHead>
                                <TableHead className="font-medium">Date livraison</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {historyByYear[year].map(o => (
                                <TableRow key={o._id}>
                                  <TableCell className="font-medium text-sm">{o.order_number}</TableCell>
                                  <TableCell><Badge className="bg-emerald-100 text-emerald-800 text-xs">{(o.status || '').toUpperCase()}</Badge></TableCell>
                                  <TableCell>
                                    {o.tracking_number ? (
                                      <Button size="sm" className="h-6 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                        onClick={() => window.open(`${window.location.origin}/track?n=${o.tracking_number}`, '_blank')}>
                                        <Package size={12} /> Suivi
                                      </Button>
                                    ) : <span className="text-xs text-slate-400">—</span>}
                                  </TableCell>
                                  <TableCell className="text-xs">{o.delivery_date ? new Date(o.delivery_date).toLocaleDateString('fr-FR') : '—'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ===== GUIDE DES TAILLES ===== */}
          {activeTab === 'size-guide' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="client-size-guide">
              <h2 style={{ color: '#c3263b', borderLeft: '5px solid #c3263b' }} className="pl-4 mb-5 uppercase text-sm font-bold">Guide des tailles</h2>
              <div className="flex gap-5 flex-wrap">
                <div className="flex-1 min-w-[280px] border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-center text-xs" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="bg-slate-50"><th colSpan={3} className="p-3 font-bold" style={{ borderBottom: '2px solid #c3263b' }}>ADULTES</th></tr>
                      <tr className="text-[10px] text-slate-500"><th className="p-2.5">TAILLE</th><th>POIDS</th><th>HAUTEUR</th></tr>
                    </thead>
                    <tbody>
                      {[['XS','45-50 kg','160-166 cm'],['S','50-55 kg','166-170 cm'],['M','55-65 kg','170-175 cm'],['L','65-75 kg','175-180 cm'],['XL','75-85 kg','180-185 cm'],['2XL','85-95 kg','185-190 cm'],['3XL','95-110 kg','190-195 cm'],['4XL','110-115 kg','195-197 cm'],['5XL','115-130 kg','197-199 cm']].map(([size, weight, height]) => (
                        <tr key={size}><td className="p-2 font-bold" style={{ color: '#c3263b' }}>{size}</td><td>{weight}</td><td>{height}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex-1 min-w-[280px] border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-center text-xs" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="bg-slate-50"><th colSpan={3} className="p-3 font-bold" style={{ borderBottom: '2px solid #c3263b' }}>ENFANTS (KIDS)</th></tr>
                      <tr className="text-[10px] text-slate-500"><th className="p-2.5">TAILLE</th><th>POIDS</th><th>HAUTEUR</th></tr>
                    </thead>
                    <tbody>
                      {[['2 ans','12-17 kg','105-122 cm'],['4 ans','17-22 kg','122-135 cm'],['6 ans','22-27 kg','135-145 cm'],['8 ans','27-35 kg','145-155 cm'],['10 ans','35-45 kg','155-160 cm']].map(([size, weight, height]) => (
                        <tr key={size}><td className="p-2 font-bold" style={{ color: '#c3263b' }}>{size}</td><td>{weight}</td><td>{height}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ===== PERSONNALISATION ===== */}
          {activeTab === 'personalization' && (
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Personnalisation</CardTitle>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={handlePersoExport} disabled={persoExporting}>
                    <Download size={14} /> {persoExporting ? 'Export...' : 'Exporter Excel'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-700"><strong>Client :</strong> {clientInfo?.firstname} {clientInfo?.name} — {clientInfo?.company_name}</p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-medium w-10">#</TableHead>
                      <TableHead className="font-medium">Nom</TableHead>
                      <TableHead className="font-medium">Numéro</TableHead>
                      <TableHead className="font-medium">Taille</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {persoLines.map((line, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs text-slate-500">{idx + 1}</TableCell>
                        <TableCell><Input value={line.nom} onChange={e => setPersoLines(prev => prev.map((l, i) => i === idx ? { ...l, nom: e.target.value } : l))} placeholder="Nom" className="h-8 text-sm" /></TableCell>
                        <TableCell><Input value={line.numero} onChange={e => setPersoLines(prev => prev.map((l, i) => i === idx ? { ...l, numero: e.target.value } : l))} placeholder="N°" className="h-8 text-sm" /></TableCell>
                        <TableCell><Input value={line.taille} onChange={e => setPersoLines(prev => prev.map((l, i) => i === idx ? { ...l, taille: e.target.value } : l))} placeholder="Taille" className="h-8 text-sm" /></TableCell>
                        <TableCell>
                          {persoLines.length > 1 && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => setPersoLines(prev => prev.filter((_, i) => i !== idx))}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Button size="sm" variant="outline" className="mt-3 gap-1" onClick={() => setPersoLines(prev => [...prev, { ...EMPTY_LINE }])}>
                  <Plus size={14} /> Ajouter une ligne
                </Button>
              </CardContent>
            </Card>
          )}

          {/* ===== CONTACT ===== */}
          {activeTab === 'contact' && (
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base">Votre commercial</CardTitle></CardHeader>
              <CardContent>
                {commercialInfo?.name ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 bg-slate-50 p-5 rounded-xl">
                      <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-lg">
                        {commercialInfo.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-slate-900">{commercialInfo.name}</p>
                        <p className="text-sm text-slate-500">Commercial Panther</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {commercialInfo.email && (
                        <div className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg">
                          <Mail size={18} className="text-slate-500" />
                          <div>
                            <p className="text-xs text-slate-500">Email</p>
                            <a href={`mailto:${commercialInfo.email}`} className="text-sm font-medium text-blue-700 hover:underline">{commercialInfo.email}</a>
                          </div>
                        </div>
                      )}
                      {commercialInfo.phone && (
                        <div className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg">
                          <Phone size={18} className="text-slate-500" />
                          <div>
                            <p className="text-xs text-slate-500">Téléphone</p>
                            <a href={`tel:${commercialInfo.phone}`} className="text-sm font-medium text-blue-700 hover:underline">{commercialInfo.phone}</a>
                          </div>
                        </div>
                      )}
                    </div>
                    <a href="https://wa.me/33652222389?text=Bonjour%20Panther%2C%20je%20viens%20de%20consulter%20mon%20compte%20client%20et%20j%27aimerais%20en%20savoir%20plus%20sur%20les%20tarifs%20et%20la%20personnalisation%20pour%20mon%20projet."
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 p-3.5 text-white font-bold rounded-lg w-full text-sm" style={{ backgroundColor: '#25D366' }}>
                      <MessageCircle size={16} /> Contacter sur WhatsApp
                    </a>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-sm text-slate-500 mb-4">Aucun commercial assigné pour le moment.</p>
                    <a href="https://wa.me/33652222389?text=Bonjour%20Panther%2C%20je%20viens%20de%20consulter%20mon%20compte%20client%20et%20j%27aimerais%20en%20savoir%20plus%20sur%20les%20tarifs%20et%20la%20personnalisation%20pour%20mon%20projet."
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 p-3.5 text-white font-bold rounded-lg text-sm px-8" style={{ backgroundColor: '#25D366' }}>
                      <MessageCircle size={16} /> Contacter Panther sur WhatsApp
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

        </main>
      </div>
    </div>
  );
}
